/**
 * useBudgetMutations Hook
 * Hook for CRUD operations on budgets with optimistic updates and cache invalidation
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Budget, BudgetFormData, BudgetEmailOptions, UseBudgetMutationsReturn } from '@/types/budget';
import { useToast } from '@/hooks/useToast';

interface CreateBudgetData extends BudgetFormData {
  // Additional fields for creation
}

interface UpdateBudgetData extends Partial<CreateBudgetData> {
  id: string;
}

interface MutateOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
  invalidateQueries?: string[][];
}

// Helper function to generate budget number
async function generateBudgetNumber(companyId: string): Promise<string> {
  const year = new Date().getFullYear();
  const prefix = `ORC-${year}`;

  const { data, error } = await supabase
    .from('budgets')
    .select('number')
    .like('number', `${prefix}-%`)
    .order('number', { ascending: false })
    .limit(1);

  if (error) {
    console.error('Error generating budget number:', error);
    return `${prefix}-001`;
  }

  const lastNumber = data?.[0]?.number;
  if (lastNumber) {
    const sequence = parseInt(lastNumber.split('-')[2]) || 0;
    const nextSequence = String(sequence + 1).padStart(3, '0');
    return `${prefix}-${nextSequence}`;
  }

  return `${prefix}-001`;
}

// Helper function to calculate totals
function calculateBudgetTotals(items: any[], discountPercentage: number = 0, discountAmount: number = 0) {
  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_value), 0);

  let discount = discountAmount;
  if (discountPercentage > 0) {
    discount = subtotal * (discountPercentage / 100);
  }

  const discountedSubtotal = subtotal - discount;

  // For now, let's assume a fixed tax rate of 0% (can be made configurable)
  const taxRate = 0;
  const taxAmount = discountedSubtotal * taxRate;
  const finalValue = discountedSubtotal + taxAmount;

  return {
    subtotal,
    discount_amount: discount,
    tax_amount: taxAmount,
    final_value: finalValue,
    total_value: finalValue
  };
}

export function useBudgetMutations(): UseBudgetMutationsReturn {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleMutationError = useCallback((error: Error, context: string) => {
    console.error(`Error ${context}:`, error);
    toast({
      title: `Erro ao ${context}`,
      description: error.message,
      variant: 'destructive'
    });
  }, [toast]);

  const handleMutationSuccess = useCallback((message: string) => {
    toast({
      title: 'Sucesso',
      description: message
    });
  }, [toast]);

  // Create budget mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateBudgetData): Promise<Budget> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get company info
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      const companyId = profile?.company_id;
      if (!companyId) {
        throw new Error('Company not found for user');
      }

      // Generate budget number
      const number = await generateBudgetNumber(companyId);

      // Calculate totals
      const totals = calculateBudgetTotals(data.items, data.discount_percentage, data.discount_amount);

      // Insert budget
      const { data: budget, error: budgetError } = await supabase
        .from('budgets')
        .insert({
          company_id: companyId,
          user_id: user.id,
          number,
          client_id: data.client_id,
          title: data.title,
          description: data.description,
          subtotal: totals.subtotal,
          discount_amount: totals.discount_amount,
          discount_percentage: data.discount_percentage || 0,
          tax_amount: totals.tax_amount,
          final_value: totals.final_value,
          total_value: totals.total_value,
          status: 'draft',
          priority: data.priority || 'medium',
          validity_type: data.validity_type,
          validity_days: data.validity_days,
          validity_date: data.validity_date,
          responsible_user_id: data.responsible_user_id,
          notes: data.notes,
          terms: data.terms,
          payment_terms: data.payment_terms,
          delivery_terms: data.delivery_terms,
          metadata: {}
        })
        .select()
        .single();

      if (budgetError) {
        throw new Error('Failed to create budget: ' + budgetError.message);
      }

      // Insert budget items
      if (data.items && data.items.length > 0) {
        const itemsToInsert = data.items.map(item => ({
          budget_id: budget.id,
          description: item.description,
          quantity: item.quantity,
          unit_value: item.unit_value,
          total_value: item.quantity * item.unit_value,
          item_type: item.item_type,
          service_id: item.service_id,
          product_id: item.product_id,
          cost_center_id: item.cost_center_id,
          category_id: item.category_id,
          notes: item.notes
        }));

        const { error: itemsError } = await supabase
          .from('budget_items')
          .insert(itemsToInsert);

        if (itemsError) {
          // Try to rollback budget creation
          await supabase.from('budgets').delete().eq('id', budget.id);
          throw new Error('Failed to create budget items: ' + itemsError.message);
        }
      }

      // Create activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: budget.id,
          user_id: user.id,
          action: 'created',
          details: { number, client_id: data.client_id }
        });

      return budget;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budgetStats'] });
      handleMutationSuccess('Orçamento criado com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'criar orçamento');
    }
  });

  // Update budget mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateBudgetData): Promise<Budget> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { id, ...updateData } = data;

      // Calculate totals if items are provided
      let totals = {};
      if (updateData.items) {
        totals = calculateBudgetTotals(updateData.items, updateData.discount_percentage, updateData.discount_amount);
      }

      // Update budget
      const { data: budget, error: budgetError } = await supabase
        .from('budgets')
        .update({
          ...updateData,
          ...totals,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (budgetError) {
        throw new Error('Failed to update budget: ' + budgetError.message);
      }

      // Update items if provided
      if (updateData.items) {
        // Delete existing items
        await supabase.from('budget_items').delete().eq('budget_id', id);

        // Insert new items
        if (updateData.items.length > 0) {
          const itemsToInsert = updateData.items.map(item => ({
            budget_id: id,
            description: item.description,
            quantity: item.quantity,
            unit_value: item.unit_value,
            total_value: item.quantity * item.unit_value,
            item_type: item.item_type,
            service_id: item.service_id,
            product_id: item.product_id,
            cost_center_id: item.cost_center_id,
            category_id: item.category_id,
            notes: item.notes
          }));

          const { error: itemsError } = await supabase
            .from('budget_items')
            .insert(itemsToInsert);

          if (itemsError) {
            throw new Error('Failed to update budget items: ' + itemsError.message);
          }
        }
      }

      // Create activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: id,
          user_id: user.id,
          action: 'updated',
          details: updateData
        });

      return budget;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget', data.id] });
      queryClient.invalidateQueries({ queryKey: ['budgetStats'] });
      handleMutationSuccess('Orçamento atualizado com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'atualizar orçamento');
    }
  });

  // Delete budget mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Check if budget can be deleted (not converted, not approved)
      const { data: budget } = await supabase
        .from('budgets')
        .select('status')
        .eq('id', id)
        .single();

      if (budget?.status === 'converted') {
        throw new Error('Cannot delete a converted budget');
      }

      if (budget?.status === 'approved') {
        throw new Error('Cannot delete an approved budget');
      }

      // Delete budget items first
      await supabase.from('budget_items').delete().eq('budget_id', id);

      // Delete budget
      const { error } = await supabase.from('budgets').delete().eq('id', id);

      if (error) {
        throw new Error('Failed to delete budget: ' + error.message);
      }

      // Create activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: id,
          user_id: user.id,
          action: 'deleted'
        });
    },
    onSuccess: (_, budgetId) => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['budgetStats'] });
      handleMutationSuccess('Orçamento excluído com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'excluir orçamento');
    }
  });

  // Duplicate budget mutation
  const duplicateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data?: Partial<CreateBudgetData> }): Promise<Budget> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get original budget with items
      const { data: originalBudget, error: fetchError } = await supabase
        .from('budgets')
        .select(`
          *,
          budget_items (*)
        `)
        .eq('id', id)
        .single();

      if (fetchError) {
        throw new Error('Failed to fetch original budget: ' + fetchError.message);
      }

      // Get company info
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      const companyId = profile?.company_id;
      if (!companyId) {
        throw new Error('Company not found for user');
      }

      // Generate new budget number
      const number = await generateBudgetNumber(companyId);

      // Create duplicate budget
      const { data: newBudget, error: createError } = await supabase
        .from('budgets')
        .insert({
          company_id: companyId,
          user_id: user.id,
          number,
          client_id: data?.client_id || originalBudget.client_id,
          title: data?.title ? `${originalBudget.title} (Cópia)` : `${originalBudget.title || ''} (Cópia)`,
          description: data?.description || originalBudget.description,
          subtotal: originalBudget.subtotal,
          discount_amount: originalBudget.discount_amount,
          discount_percentage: originalBudget.discount_percentage,
          tax_amount: originalBudget.tax_amount,
          final_value: originalBudget.final_value,
          total_value: originalBudget.total_value,
          status: 'draft',
          priority: data?.priority || originalBudget.priority,
          validity_type: data?.validity_type || originalBudget.validity_type,
          validity_days: data?.validity_days || originalBudget.validity_days,
          validity_date: data?.validity_date || originalBudget.validity_date,
          responsible_user_id: data?.responsible_user_id || originalBudget.responsible_user_id,
          notes: data?.notes || originalBudget.notes,
          terms: data?.terms || originalBudget.terms,
          payment_terms: data?.payment_terms || originalBudget.payment_terms,
          delivery_terms: data?.delivery_terms || originalBudget.delivery_terms,
          metadata: originalBudget.metadata
        })
        .select()
        .single();

      if (createError) {
        throw new Error('Failed to duplicate budget: ' + createError.message);
      }

      // Duplicate budget items
      if (originalBudget.budget_items && originalBudget.budget_items.length > 0) {
        const itemsToInsert = originalBudget.budget_items.map(item => ({
          budget_id: newBudget.id,
          description: item.description,
          quantity: item.quantity,
          unit_value: item.unit_value,
          total_value: item.total_value,
          item_type: item.item_type,
          service_id: item.service_id,
          product_id: item.product_id,
          cost_center_id: item.cost_center_id,
          category_id: item.category_id,
          notes: item.notes
        }));

        const { error: itemsError } = await supabase
          .from('budget_items')
          .insert(itemsToInsert);

        if (itemsError) {
          // Try to rollback budget creation
          await supabase.from('budgets').delete().eq('id', newBudget.id);
          throw new Error('Failed to duplicate budget items: ' + itemsError.message);
        }
      }

      // Create activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: newBudget.id,
          user_id: user.id,
          action: 'created',
          details: {
            duplicated_from: id,
            original_number: originalBudget.number
          }
        });

      return newBudget;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budgetStats'] });
      handleMutationSuccess('Orçamento duplicado com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'duplicar orçamento');
    }
  });

  // Approve budget mutation
  const approveMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }): Promise<Budget> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: budget, error } = await supabase
        .from('budgets')
        .update({
          status: 'approved',
          approved_by_user_id: user.id,
          approved_at: new Date().toISOString(),
          notes: notes
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error('Failed to approve budget: ' + error.message);
      }

      // Create activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: id,
          user_id: user.id,
          action: 'approved',
          details: { notes }
        });

      return budget;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget', data.id] });
      queryClient.invalidateQueries({ queryKey: ['budgetStats'] });
      handleMutationSuccess('Orçamento aprovado com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'aprovar orçamento');
    }
  });

  // Reject budget mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }): Promise<Budget> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: budget, error } = await supabase
        .from('budgets')
        .update({
          status: 'rejected',
          rejected_by_user_id: user.id,
          rejected_at: new Date().toISOString(),
          rejected_reason: reason
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error('Failed to reject budget: ' + error.message);
      }

      // Create activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: id,
          user_id: user.id,
          action: 'rejected',
          details: { reason }
        });

      return budget;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget', data.id] });
      queryClient.invalidateQueries({ queryKey: ['budgetStats'] });
      handleMutationSuccess('Orçamento rejeitado com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'rejeitar orçamento');
    }
  });

  // Convert budget to transaction mutation
  const convertMutation = useMutation({
    mutationFn: async ({ id, transactionData }: { id: string; transactionData?: any }): Promise<any> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get budget details
      const { data: budget } = await supabase
        .from('budgets')
        .select('*')
        .eq('id', id)
        .single();

      if (!budget) {
        throw new Error('Budget not found');
      }

      // This would integrate with the transaction system
      // For now, we'll just mark the budget as converted
      const { data: updatedBudget, error } = await supabase
        .from('budgets')
        .update({
          status: 'converted',
          conversion_date: new Date().toISOString(),
          conversion_transaction_id: transactionData?.id || null
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error('Failed to convert budget: ' + error.message);
      }

      // Create activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: id,
          user_id: user.id,
          action: 'converted',
          details: { transaction_id: transactionData?.id }
        });

      return updatedBudget;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget', data.id] });
      queryClient.invalidateQueries({ queryKey: ['budgetStats'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] }); // Invalidate transactions if created
      handleMutationSuccess('Orçamento convertido com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'converter orçamento');
    }
  });

  // Send budget by email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async ({ id, options }: { id: string; options: BudgetEmailOptions }): Promise<void> => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw new Error('Authentication error: ' + userError.message);
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      // This would integrate with an email service
      // For now, we'll just create an activity log
      await supabase
        .from('budget_activity')
        .insert({
          budget_id: id,
          user_id: user.id,
          action: 'sent',
          details: {
            to: options.to,
            subject: options.subject,
            include_pdf: options.includePdf,
            include_items: options.includeItems
          }
        });

      // Simulate email sending
      console.log('Sending budget email:', { id, options });
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['budget', id] });
      handleMutationSuccess('Orçamento enviado por e-mail com sucesso!');
    },
    onError: (error) => {
      handleMutationError(error, 'enviar orçamento por e-mail');
    }
  });

  return {
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
    duplicate: duplicateMutation.mutateAsync,
    approve: approveMutation.mutateAsync,
    reject: rejectMutation.mutateAsync,
    convert: convertMutation.mutateAsync,
    sendByEmail: sendEmailMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isConverting: convertMutation.isPending,
    isSending: sendEmailMutation.isPending
  };
}